import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { type TaskContentProps, useOnboardingTaskProgress } from '@frontend/product-onboarding';
import { Text, TextLink } from '@frontend/design-system';
import { CompletionBanner } from './completion-banner';
import OnboardingChecklist from './onboarding-checklist';
import { ProgressBar } from './progress-bar';

const WEAVE_HELP_URL = 'https://www.weavehelp.com/weavehelp/article/call-intelligence-standard-and-pro-plans';

export type OnboardingTaskAction = {
  isEnabled: boolean;
  isLoading?: boolean;
  onSelect?: () => void;
};

type OnboardingTaskContentViewProps<TaskId extends string> = TaskContentProps & {
  title: string;
  completedGroups: TaskId[];
  actions: Partial<Record<TaskId, OnboardingTaskAction>>;
};

export function OnboardingTaskContentView<TaskId extends string>({
  title,
  onClose,
  completedHotspots,
  flow,
  completedGroups,
  actions,
}: OnboardingTaskContentViewProps<TaskId>) {
  const { t } = useTranslation('call-intel-onboarding');

  const { tasks, completedCount, totalCount, progressPercent, isAllCompleted } = useOnboardingTaskProgress({
    flow,
    completedHotspots,
    completedGroups,
  });

  return (
    <OnboardingChecklist>
      <OnboardingChecklist.Header title={title} onClose={onClose}>
        <Text size='small' color='light'>
          {t('{{completed}} of {{total}} Completed', { completed: completedCount, total: totalCount })}
        </Text>
      </OnboardingChecklist.Header>

      <OnboardingChecklist.Body>
        <ProgressBar progress={progressPercent} />
        {isAllCompleted && <CompletionBanner />}
        <OnboardingChecklist.TaskList>
          {tasks.map((task) => {
            const action = actions[task.id as TaskId];

            return (
              <OnboardingChecklist.Task
                key={task.id}
                completed={task.isCompleted}
                disabled={!action?.isEnabled}
                loading={action?.isLoading}
                onSelect={action?.onSelect}
                description={task.description ?? ''}
                label={task.label}
                trackingId={task.tracking?.navigationTrackingId}
              />
            );
          })}
        </OnboardingChecklist.TaskList>

        <Text as='span' size='small' color='light'>
          {t('Have additional questions? Visit')}{' '}
          <TextLink href={WEAVE_HELP_URL} target='_blank' rel='noreferrer' css={styles.helpLink}>
            {t('Weave Help')}
          </TextLink>
          .
        </Text>
      </OnboardingChecklist.Body>
    </OnboardingChecklist>
  );
}

const styles = {
  helpLink: css`
    text-decoration: underline;
  `,
};
